import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	Chip,
	CircularProgress,
	FormControlLabel,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { makeStyles } from "tss-react/mui";
import { useDispatch } from "react-redux";
import AccordionBox from "components/Layouts/AccordionBox";
import NavDetails from "components/Elements/NavDetails";
import TabTitle from "components/Elements/TabTitle";
import ActionButtonStyle from "styles/application/ActionButtonStyle";
import ContentStyle from "styles/application/ContentStyle";
import ColourConstants from "helpers/colourConstants";
import {
	createAIConfiguration,
	getAIConfigurationById,
	getAIConfigurations,
	testAIConfiguration,
	getAIProviders,
	updateAIConfiguration,
} from "services/ai";
import { showError, showSuccess } from "../../redux/common/actions";

const AC = ContentStyle();
const AT = ActionButtonStyle();

const useStyles = makeStyles()(() => ({
	page: {
		padding: 24,
	},
	topRow: {
		display: "flex",
		alignItems: "flex-start",
		gap: 16,
		flexWrap: "wrap",
		marginBottom: 16,
	},
	titleRow: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginBottom: 8,
	},
	titleIcon: {
		color: ColourConstants.activeLink,
		fontSize: 28,
	},
	titleText: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "28px",
		color: ColourConstants.commonText,
	},
	actions: {
		marginLeft: "auto",
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 10,
	},
	switchLabel: {
		marginRight: 8,
		"& .MuiFormControlLabel-label": {
			fontFamily: "Roboto",
			fontSize: "13.5px",
			color: ColourConstants.commonText,
		},
	},
	multiConfigNote: {
		fontSize: 13,
		color: ColourConstants.commonNoteText,
		marginTop: 6,
	},
	fieldWrap: {
		marginBottom: 17,
		width: "100%",
	},
	labelText: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "14px",
	},
	requiredStar: {
		color: ColourConstants.requiredStar,
		marginLeft: 2,
	},
	inputText: {
		fontSize: 14,
		color: "#000000de",
	},
	textAreaInput: {
		fontSize: 14,
		color: "#000000de",
		lineHeight: 1.4,
	},
	apiKeyAction: {
		width: "100%",
	},
	apiKeySavedHint: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		marginTop: 6,
		fontSize: 13,
		color: ColourConstants.confirmButton,
	},
	metaRow: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		marginTop: 8,
		color: ColourConstants.commonNoteText,
		fontSize: 13,
	},
}));

const FEATURE_OPTIONS = [{ label: "Translation", value: "translation" }];

const DEFAULT_CONFIG = {
	providerType: "anthropic",
	featureType: "translation",
	modelName: "",
	apiKey: "",
	endpointUrl: "",
	apiVersion: "",
	translationSystemPrompt: "",
	isActive: false,
};

const normaliseValue = (value) => {
	if (value === null || value === undefined) return "";
	return value;
};

const buildPatches = (draft, original, includeApiKeyUpdate) => {
	const fields = [
		"providerType",
		"featureType",
		"modelName",
		"endpointUrl",
		"apiVersion",
		"translationSystemPrompt",
		"isActive",
	];

	return fields.reduce((patches, field) => {
		if (normaliseValue(draft[field]) !== normaliseValue(original[field])) {
			patches.push({
				op: "replace",
				path: `/${field}`,
				value: draft[field] ?? "",
			});
		}
		return patches;
	}, includeApiKeyUpdate && draft.apiKey
		? [{ op: "replace", path: "/apiKey", value: draft.apiKey }]
		: []);
};

const AIConfigurationDetailPage = () => {
	const { classes } = useStyles();
	const dispatch = useDispatch();

	const [configId, setConfigId] = useState(null);
	const [originalConfig, setOriginalConfig] = useState(null);
	const [draftConfig, setDraftConfig] = useState(null);
	const [providers, setProviders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [multipleConfigsFound, setMultipleConfigsFound] = useState(false);
	const [showApiKey, setShowApiKey] = useState(false);
	const [connectionStatus, setConnectionStatus] = useState({
		state: "idle",
		message: "",
	});

	const runConnectionTest = useCallback(
		async (idToTest) => {
			if (!idToTest) return;
			setConnectionStatus({ state: "testing", message: "" });
			const res = await testAIConfiguration(idToTest);
			if (res.status) {
				setConnectionStatus({
					state: "connected",
					message: res.data?.message ?? "Connection successful",
				});
			} else {
				setConnectionStatus({
					state: "failed",
					message: res.data?.message ?? "Connection test failed",
				});
			}
		},
		[]
	);

	const fetch = useCallback(async () => {
		setLoading(true);
		const [configsRes, providersRes] = await Promise.all([
			getAIConfigurations(),
			getAIProviders(),
		]);

		if (configsRes?.status) {
			const configs = configsRes.data ?? [];
			const primaryConfig = configs[0] ?? null;

			setMultipleConfigsFound(configs.length > 1);
			setConfigId(primaryConfig?.id ?? null);

			if (primaryConfig?.id) {
				const configRes = await getAIConfigurationById(primaryConfig.id);
				if (configRes?.status) {
					const nextConfig = {
						...DEFAULT_CONFIG,
						...configRes.data,
						apiKey: "",
					};
					setOriginalConfig(nextConfig);
					setDraftConfig(nextConfig);
				} else {
					setOriginalConfig(null);
					setDraftConfig({ ...DEFAULT_CONFIG });
					dispatch(showError("Failed to load AI configuration"));
				}
			} else {
				setOriginalConfig(null);
				setDraftConfig({ ...DEFAULT_CONFIG });
			}
		} else {
			setOriginalConfig(null);
			setDraftConfig({ ...DEFAULT_CONFIG });
			dispatch(showError("Failed to load AI configuration"));
		}

		if (providersRes?.status) {
			setProviders(providersRes.data ?? []);
		}

		setLoading(false);
	}, [dispatch]);

	useEffect(() => {
		fetch();
	}, [fetch]);

	useEffect(() => {
		if (configId && originalConfig?.isActive && originalConfig?.hasApiKey) {
			runConnectionTest(configId);
		}
	}, [configId, originalConfig?.isActive, originalConfig?.hasApiKey, runConnectionTest]);

	const currentConfig = draftConfig ?? DEFAULT_CONFIG;

	const providerOptions = useMemo(
		() =>
			providers.map((provider) => ({
				label: provider.displayName || provider.providerType,
				value: provider.providerType,
			})),
		[providers]
	);

	const isDirty = useMemo(() => {
		if (!draftConfig) return false;
		if (!originalConfig) {
			return (
				!!draftConfig.modelName?.trim() ||
				!!draftConfig.apiKey?.trim() ||
				!!draftConfig.endpointUrl?.trim() ||
				!!draftConfig.apiVersion?.trim() ||
				!!draftConfig.translationSystemPrompt?.trim() ||
				draftConfig.providerType !== DEFAULT_CONFIG.providerType ||
				draftConfig.featureType !== DEFAULT_CONFIG.featureType ||
				draftConfig.isActive !== DEFAULT_CONFIG.isActive
			);
		}

		return buildPatches(draftConfig, originalConfig, true).length > 0;
	}, [draftConfig, originalConfig]);

	const updateField = (field, value) => {
		setDraftConfig((prev) => ({ ...(prev ?? DEFAULT_CONFIG), [field]: value }));
	};

	const handleCopyEndpoint = async () => {
		if (!currentConfig.endpointUrl) return;
		try {
			await navigator.clipboard.writeText(currentConfig.endpointUrl);
			dispatch(showSuccess("Endpoint URL copied"));
		} catch {
			dispatch(showError("Could not copy endpoint URL"));
		}
	};

	const handleSave = async () => {
		if (!draftConfig) return;

		if (!configId && !draftConfig.apiKey?.trim()) {
			dispatch(showError("API key is required"));
			return;
		}
		if (!draftConfig.modelName?.trim()) {
			dispatch(showError("Model name is required"));
			return;
		}

		const patches = originalConfig
			? buildPatches(draftConfig, originalConfig, true)
			: [];
		if (configId && patches.length === 0) return;

		setSaving(true);
		try {
			const response = configId
				? await updateAIConfiguration(configId, patches)
				: await createAIConfiguration({ ...draftConfig });

			if (response.status) {
				dispatch(
					showSuccess(
						configId
							? "AI configuration updated"
							: "AI configuration created"
					)
				);
				const savedId = configId ?? response.data;
				await fetch();
				if (draftConfig.isActive && savedId) {
					await runConnectionTest(savedId);
				}
			} else {
				dispatch(
					showError(
						response.data?.message ??
							(configId
								? "Failed to update AI configuration"
								: "Failed to create AI configuration")
					)
				);
			}
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<AC.SpinnerContainer>
				<CircularProgress />
			</AC.SpinnerContainer>
		);
	}

	return (
		<div className={classes.page}>
			<TabTitle title="AI Configuration" />

			<div className={classes.topRow}>
				<div>
					<div className={classes.titleRow}>
						<Typography className={classes.titleText}>
							AI Configuration
						</Typography>
					</div>
					<NavDetails
						history={false}
						hideLastSave={true}
						staticCrumbs={[
							{ id: 1, name: "AI Configuration", url: "/app/aiconfiguration" },
						]}
					/>
					{multipleConfigsFound && (
						<Typography className={classes.multiConfigNote}>
							Multiple AI configurations exist. This page is editing the first one returned by the API.
						</Typography>
					)}
					{!configId && (
						<Typography className={classes.multiConfigNote}>
							No configuration exists yet. Fill in the form and save to create it.
						</Typography>
					)}
				</div>

				<div className={classes.actions}>
					<Tooltip title={connectionStatus.message || ""} placement="bottom">
						<Chip
							size="small"
							icon={
								connectionStatus.state === "testing" ? (
									<CircularProgress size={14} />
								) : connectionStatus.state === "connected" ? (
									<CheckCircleOutlineRoundedIcon />
								) : connectionStatus.state === "failed" ? (
									<ErrorOutlineRoundedIcon />
								) : (
									<RadioButtonUncheckedRoundedIcon />
								)
							}
							label={
								connectionStatus.state === "testing"
									? "Testing connection…"
									: connectionStatus.state === "connected"
									? "Connected"
									: connectionStatus.state === "failed"
									? "Connection failed"
									: "Not tested"
							}
							color={
								connectionStatus.state === "connected"
									? "success"
									: connectionStatus.state === "failed"
									? "error"
									: "default"
							}
							variant={
								connectionStatus.state === "idle" ? "outlined" : "filled"
							}
						/>
					</Tooltip>
					<Button
						size="small"
						variant="outlined"
						onClick={() => runConnectionTest(configId)}
						disabled={
							!configId ||
							connectionStatus.state === "testing" ||
							saving
						}
					>
						Test Now
					</Button>
					<FormControlLabel
						className={classes.switchLabel}
						control={
							<Switch
								checked={!!currentConfig.isActive}
								onChange={(event) =>
									updateField("isActive", event.target.checked)
								}
							/>
						}
						label={currentConfig.isActive ? "Active" : "Inactive"}
					/>
					<AT.GeneralButton onClick={handleSave} disabled={!isDirty || saving}>
						{saving ? "Saving..." : "Save Changes"}
					</AT.GeneralButton>
				</div>
			</div>

			<AccordionBox title="AI Configuration" noExpand={true}>
				<Grid container spacing={3}>
					<Grid item xs={12} md={6}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								Provider Type<span className={classes.requiredStar}>*</span>
							</Typography>
							<TextField
								select
								fullWidth
								value={currentConfig.providerType ?? ""}
								onChange={(event) =>
									updateField("providerType", event.target.value)
								}
								InputProps={{ classes: { input: classes.inputText } }}
							>
								{providerOptions.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</div>
					</Grid>

					<Grid item xs={12} md={6}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								Endpoint URL<span className={classes.requiredStar}>*</span>
							</Typography>
							<TextField
								fullWidth
								placeholder="Optional"
								value={currentConfig.endpointUrl ?? ""}
								onChange={(event) =>
									updateField("endpointUrl", event.target.value)
								}
								InputProps={{
									classes: { input: classes.inputText },
									endAdornment: (
										<InputAdornment position="end">
											<ContentCopyRoundedIcon
												onClick={handleCopyEndpoint}
												sx={{
													fontSize: 18,
													color: ColourConstants.activeLink,
													cursor: currentConfig.endpointUrl
														? "pointer"
														: "default",
												}}
											/>
										</InputAdornment>
									),
								}}
							/>
						</div>
					</Grid>

					<Grid item xs={12} md={6}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								Feature Type<span className={classes.requiredStar}>*</span>
							</Typography>
							<TextField
								select
								fullWidth
								value={currentConfig.featureType ?? ""}
								onChange={(event) =>
									updateField("featureType", event.target.value)
								}
								InputProps={{ classes: { input: classes.inputText } }}
							>
								{FEATURE_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</div>
					</Grid>

					<Grid item xs={12} md={6}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								API Key<span className={classes.requiredStar}>*</span>
							</Typography>
							<div className={classes.apiKeyAction}>
								<TextField
									fullWidth
									type={showApiKey ? "text" : "password"}
									placeholder={
										originalConfig?.hasApiKey
											? "•••••••••• Saved — enter a new key to replace"
											: "Enter API key"
									}
									value={currentConfig.apiKey ?? ""}
									onChange={(event) =>
										updateField("apiKey", event.target.value)
									}
									autoComplete="new-password"
									InputProps={{
										classes: { input: classes.inputText },
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													size="small"
													onClick={() =>
														setShowApiKey((prev) => !prev)
													}
													edge="end"
												>
													{showApiKey ? (
														<VisibilityOffOutlinedIcon fontSize="small" />
													) : (
														<VisibilityOutlinedIcon fontSize="small" />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
								{originalConfig?.hasApiKey && (
									<div className={classes.apiKeySavedHint}>
										<CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />
										<span>API key is saved. Leave blank to keep it.</span>
									</div>
								)}
							</div>
						</div>
					</Grid>

					<Grid item xs={12} md={6}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								Model Name<span className={classes.requiredStar}>*</span>
							</Typography>
							<TextField
								fullWidth
								value={currentConfig.modelName ?? ""}
								onChange={(event) =>
									updateField("modelName", event.target.value)
								}
								InputProps={{ classes: { input: classes.inputText } }}
							/>
						</div>
					</Grid>

					<Grid item xs={12} md={6}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								API Version
							</Typography>
							<TextField
								fullWidth
								placeholder="Optional"
								value={currentConfig.apiVersion ?? ""}
								onChange={(event) =>
									updateField("apiVersion", event.target.value)
								}
								InputProps={{ classes: { input: classes.inputText } }}
							/>
						</div>
					</Grid>

					<Grid item xs={12}>
						<div className={classes.fieldWrap}>
							<Typography gutterBottom className={classes.labelText}>
								Translation System Prompt
							</Typography>
							<TextField
								fullWidth
								multiline
								minRows={10}
								placeholder="Leave blank to use the built-in default prompt."
								value={currentConfig.translationSystemPrompt ?? ""}
								onChange={(event) =>
									updateField(
										"translationSystemPrompt",
										event.target.value
									)
								}
								InputProps={{
									classes: { input: classes.textAreaInput },
								}}
							/>
							<div className={classes.metaRow}>
								<InfoOutlinedIcon sx={{ fontSize: 16 }} />
								<span>
									System prompt sent to the AI translator. Use{" "}
									<code>{`{TARGET_LOCALE}`}</code> as a placeholder for the
									target locale (e.g. <code>es-MX</code>). Glossary entries
									are prepended automatically — only put translation
									instructions here.
								</span>
							</div>
						</div>
					</Grid>
				</Grid>
			</AccordionBox>
		</div>
	);
};

export default AIConfigurationDetailPage;
